import { createClient } from '@supabase/supabase-js';

// Credenciales de Supabase desde variables de entorno o valores por defecto
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://lpxmpqpmoofsrlbenatl.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxweG1wcXBtb29mc3JsYmVuYXRsIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0OTk5OTc4NywiZXhwIjoyMDY1NTc1Nzg3fQ.D9HWupkzbKkX6i6xanLDC5vCBhQsS5Jx3wx3jrjJUvU';

// Variables para el webhook de Discord
export const DISCORD_WEBHOOK_URL = process.env.REACT_APP_DISCORD_WEBHOOK_URL || 'https://api.tusecreto.net/webhook/global-secret';
export const DISCORD_WEBHOOK_TOKEN = process.env.REACT_APP_DISCORD_WEBHOOK_TOKEN || 'da84f483fcc54b94d3e7b736e4368782609d55dcf30f7a79';

// Crear cliente de Supabase
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Función para extraer el token de acceso de la URL
export const extractTokenFromUrl = () => {
  try {
    const hash = window.location.hash;
    if (!hash || !hash.includes('access_token')) return null;
    
    const tokenPart = hash.split('&').find(part => 
      part.startsWith('#access_token=') || part.startsWith('access_token=')
    );
    
    if (!tokenPart) return null;
    
    return tokenPart.replace('#access_token=', '').replace('access_token=', '');
  } catch (e) {
    console.error('Error al extraer el token de la URL:', e);
    return null;
  }
};

// Función para establecer manualmente la sesión con un token
export const setSessionWithToken = async (accessToken) => {
  if (!accessToken) return { error: { message: 'Token no proporcionado' } };
  
  try {
    console.log('Estableciendo sesión con token...');
    
    // Extraer información del token (es un JWT)
    const tokenParts = accessToken.split('.');
    if (tokenParts.length !== 3) {
      return { error: { message: 'Formato de token inválido' } };
    }
    
    // Decodificar la parte de payload del token
    try {
      const payload = JSON.parse(atob(tokenParts[1]));
      console.log('Información del token:', {
        exp: new Date(payload.exp * 1000).toISOString(),
        sub: payload.sub,
        email: payload.email,
        role: payload.role
      });
    } catch (e) {
      console.warn('No se pudo decodificar el token', e);
    }
    
    // Intentar iniciar sesión directamente con el token
    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: null
    });
    
    if (error) {
      console.error('Error al establecer la sesión:', error);
      
      // Intentar un enfoque alternativo
      console.log('Intentando enfoque alternativo...');
      const authResponse = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=id_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify({ access_token: accessToken })
      });
      
      if (!authResponse.ok) {
        const errorData = await authResponse.json();
        console.error('Error en enfoque alternativo:', errorData);
        return { error };
      }
      
      const authData = await authResponse.json();
      console.log('Respuesta de enfoque alternativo:', authData);
      
      // Verificar si se estableció la sesión correctamente
      const sessionCheck = await supabase.auth.getSession();
      if (sessionCheck.data && sessionCheck.data.session) {
        return { data: sessionCheck.data };
      }
      
      return { error };
    }
    
    if (data && data.session) {
      console.log('Sesión establecida correctamente');
      return { data };
    } else {
      console.error('No se pudo establecer la sesión');
      return { error: { message: 'No se pudo establecer la sesión' } };
    }
  } catch (e) {
    console.error('Error inesperado al establecer la sesión:', e);
    return { error: e };
  }
};

export const signInWithDiscord = async () => {
  try {
    console.log('Iniciando autenticación con Discord...');
    
    // Usar la URL de redirección local para el panel de administración
    const redirectTo = window.location.origin + '/admin';
    console.log('URL de redirección:', redirectTo);
    
    // Configurar opciones específicas para Discord con scopes explícitos
    const options = {
      provider: 'discord',
      options: {
        redirectTo: redirectTo,
        scopes: 'identify email guilds', // Asegurarnos de incluir 'guilds'
        queryParams: {
          prompt: 'consent', // Forzar el diálogo de consentimiento para asegurar que se aceptan todos los permisos
        }
      }
    };
    
    // Usar el método estándar para la autenticación de OAuth
    const { data, error } = await supabase.auth.signInWithOAuth(options);
    
    if (error) {
      console.error('Error al iniciar sesión con Discord:', error.message);
      return { error };
    }
    
    console.log('Redirección a Discord iniciada:', data);
    return { data };
  } catch (e) {
    console.error('Error inesperado durante la autenticación:', e);
    return { error: e };
  }
};

export const signOut = async () => {
  try {
    console.log('Cerrando sesión...');
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error al cerrar sesión:', error.message);
      return { error };
    }
    
    console.log('Sesión cerrada correctamente');
    return { success: true };
  } catch (e) {
    console.error('Error inesperado al cerrar sesión:', e);
    return { error: e };
  }
};

export const getCurrentUser = async () => {
  try {
    console.log('Obteniendo sesión actual...');
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Error al obtener la sesión:', error.message);
      return { error };
    }
    
    if (!session) {
      console.log('No hay sesión activa');
      return { user: null };
    }
    
    console.log('Sesión activa encontrada:', session.user.email);
    return { user: session.user };
  } catch (e) {
    console.error('Error inesperado al obtener usuario actual:', e);
    return { error: e };
  }
};

export const getUserServers = async () => {
  const { user } = await getCurrentUser();
  
  if (!user) {
    return { error: 'Usuario no autenticado' };
  }
  
  // Obtener los servidores donde el usuario tiene permisos de administrador
  const { data, error } = await supabase
    .from('server_config')
    .select('*')
    .eq('userid', user.id);
  
  if (error) {
    console.error('Error al obtener servidores:', error.message);
    return { error };
  }
  
  return { servers: data || [] };
}; 